// =============================================================================
// Button components
// =============================================================================

import { forwardRef, type ButtonHTMLAttributes } from "react";
import clsx from "clsx";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
  icon?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = "primary", loading, icon, children, className, disabled, ...props }, ref) => {
    const variantClasses: Record<ButtonVariant, string> = {
      primary: "btn-primary",
      secondary: "btn-secondary",
      ghost: "btn-ghost",
      danger: "btn-danger",
    };

    return (
      <button
        ref={ref}
        className={clsx(variantClasses[variant], className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
            <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          </span>
        ) : icon ? (
          <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center [&_svg]:h-4 [&_svg]:w-4">
            {icon}
          </span>
        ) : null}
        {children != null && children !== false ? (
          <span className="inline-flex items-center gap-[7px] leading-none">{children}</span>
        ) : null}
      </button>
    );
  }
);
Button.displayName = "Button";

export function IconButton({
  children,
  className,
  label,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label?: string }) {
  return (
    <button className={clsx("btn-icon", className)} aria-label={label} {...props}>
      {children}
    </button>
  );
}

export function FabButton({
  children,
  className,
  label,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label?: string }) {
  return (
    <button className={clsx("btn-fab", className)} aria-label={label} {...props}>
      {children}
    </button>
  );
}
